import React, {Fragment} from "react";
import PropTypes from 'prop-types';
import TextInput from "./TextInput";
import ComponentConfig from "./ComponentConfig";
import {get} from "../../utils/dotutils";
import {EditorField as Editor} from "./editor/editor";
import TextArea from "./TextArea";
import Select from "./Select";
import {optionsTemplate} from "../../utils/formatters";
import AddressControl from "./AddressControl";
import DocumentInput from "./DocumentInput";
import SelectorGrid from "./SelectorGrid/SelectorGrid";
import isEqual from "lodash-es/isEqual";
import {getComponentConfig, getFieldConfig} from "../../utils/components";
import ContentLoader from "../layout/ContentLoader";
import UrlField from "./UrlField";
import Figures from "./Figures";
import PriorityField from "./PriorityField";
import {ProductClassSelect} from "../editors/ProductClassEditor";
import CheckBox from "./CheckBox";
import SeoField from "./SeoField";

export default class ComponentField extends React.Component {

    static propTypes = {
        name: PropTypes.string.isRequired,
        values: PropTypes.any.isRequired,
        componentConfig: PropTypes.any,
        fieldDef: PropTypes.any,
        getFieldProps: PropTypes.func,
        label: PropTypes.any,
        fieldType: PropTypes.string,
        fieldEditorType: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        defaultType: PropTypes.string,
        defaultTypeOverride: PropTypes.string,
        typeOverrides: PropTypes.any,
        disabled: PropTypes.oneOf([true, false, "true", "false", "disabled"]),
    }

    constructor(props) {
        super(props);
        this.state = {
            isLoading: !this.props.componentConfig.fielddefs[this.props.name],
            componentConfig: this.props.componentConfig,
        }
        this.init();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.name !== prevProps.name || !isEqual(this.props.componentConfig, prevProps.componentConfig)) {
            this.init();
        }
    }

    loadConfig = async (config, field) => {
        return getFieldConfig(config, field);
    }

    init = () => {
        if (!this.props.componentConfig[this.props.name]) {
            if (!this.state.isLoading) {
                this.setState({isLoading: true}, () => {
                    this.loadConfig(this.props.componentConfig, this.props.name).then((config) => {
                        this.setState({componentConfig: config, isLoading: false});
                    });
                });
            } else {
                this.loadConfig(this.props.componentConfig, this.props.name).then((config) => {
                    this.setState({componentConfig: config, isLoading: false});
                });
            }
        } else {
            this.setState({componentConfig: this.props.componentConfig, isLoading: false});
        }
    }

    getFieldProps = (name, props) => {
        let fieldProps = {...props};
        if (this.props.getFieldProps) {
            fieldProps = this.props.getFieldProps(name);
        }
        fieldProps.disabled = fieldProps.disabled || props.disabled;
        return fieldProps;
    }

    setFieldValue = (name, value) => {
        if (this.props.setFieldValue) {
            this.props.setFieldValue(this.props.name || this.props.field, value);
        }
    }

    filterProps = (props) => {
        const filterProps = {...props};
        if(filterProps.saveChanges !== undefined) {
            delete filterProps.saveChanges;
        }
        if (filterProps.getFieldProps !== undefined) {
            delete filterProps.getFieldProps;
        }
        if (filterProps.setFieldValue !== undefined) {
            delete filterProps.setFieldValue;
        }
        return filterProps;
    }

    render() {
        if (this.state.isLoading) {
            return <ContentLoader />;
        }
        const componentConfig = this.state.componentConfig;
        if (!componentConfig) {
            return null;
        }
        const name = this.props.name;
        const field = this.props.field || this.props.name;
        const values = this.props.values;
        const idfield = componentConfig.idfield || "id"; // id fallback but not needed!
        const props = this.filterProps({...this.props});
        let type = this.props.fieldType;
        let FieldEditorType = this.props.fieldEditorType;
        const defaultTypeOverride = this.props.defaultTypeOverride;
        const defaultType = this.props.defaultType;
        /*if (name && componentConfig.fielddefs && !componentConfig.fielddefs[field]) {
            if (field.indexOf(".") !== -1) {
                const field = field.split(".");
                const fieldName = field.shift();
                if (componentConfig.fielddefs[fieldName]) {
                    type = type || componentConfig.fielddefs[fieldName].type || defaultType;
                    if (type === "attribute") {
                        type = type || defaultTypeOverride;
                    }
                    if (["object", "objects"].indexOf(type) !== -1) {
                        return <ComponentConfig componentName={componentConfig.fielddefs[fieldName].object || componentConfig.fielddefs[fieldName].objects}>
                            <ComponentField
                                {...props}
                                field={field.join(".")}
                            />
                        </ComponentConfig>;
                    }
                } else {
                    console.error(`No '${fieldName}' fielddef in '${componentConfig.componentname}'!`);
                    return null;
                }
            }
        }*/
        const fielddefs = this.props.fieldDef || componentConfig.fielddefs[name];
        if (name && fielddefs) {
            type = type || fielddefs.type || defaultType;
            if (type === "attribute") {
                type = type || defaultTypeOverride;
            }
            switch (type) {
                default:
                case "attribute":
                    if (idfield === name) {
                        props.role = "idfield";
                    }
                    FieldEditorType = FieldEditorType || TextInput;
                    return (
                        <div className="form-group">
                            {FieldEditorType === CheckBox ? <label className="control-label">&nbsp;</label> : null}
                            <FieldEditorType
                                {...props}
                                {...this.getFieldProps(name, props)}
                                name={name}
                                value={get(values, name, undefined)}
                                label={props.label || name}
                                saveChanges={this.setFieldValue}
                            />
                        </div>
                    );
                case "text":
                    return (
                        <div className="form-group">
                            <TextArea
                                {...props}
                                {...this.getFieldProps(name, props)}
                                name={name}
                                value={get(values, name, undefined)}
                                label={props.label || name}
                                saveChanges={this.setFieldValue}
                            />
                        </div>
                    );
                case "xmlelement":
                    return (
                        <div className="form-group">
                            <Editor
                                {...props}
                                {...this.getFieldProps(name, props)}
                                name={name}
                                value={get(values, name, undefined)}
                                label={props.label || name}
                                saveChanges={this.setFieldValue}
                                component={props.componentConfig.componentname}
                                componentId={props.values[props.componentConfig.idfield]}
                                height="200px"
                            />
                        </div>
                    );
                case "skeleton":
                case "object":
                    switch (fielddefs.object) {
                        case "ProductClass":
                            return (
                                <Fragment>
                                    <label className="control-label">
                                        <label className="control-label">{props.label || name || fielddefs.object}</label>
                                    </label>
                                    <ProductClassSelect
                                        selectionMode="single"
                                        value={get(values, name, undefined)}
                                        saveChanges={this.setFieldValue}
                                        name={name}
                                    />
                                </Fragment>
                            );
                        case "document":
                            return (
                                <div className="form-group">
                                    <DocumentInput
                                        label={props.label || name}
                                        name={name}
                                        componentConfig={props.componentConfig}
                                        values={props.values}
                                        {...props}
                                        {...this.getFieldProps(name, props)}
                                        saveChanges={this.setFieldValue}
                                    />
                                </div>
                            );
                        case "address":
                            return (
                                <div className="form-group">
                                    <AddressControl
                                        {...props}
                                        {...this.getFieldProps(name, props)}
                                        value={get(values, name, undefined)}
                                        label={props.label || "Cím"}
                                        saveChanges={this.setFieldValue}
                                    />
                                </div>
                            );
                        default:
                            props.labelKey = props.labelKey || ((object) => {
                                switch(object) {
                                    case "partner":
                                        return "name";
                                }
                            })(fielddefs.object);
                            return (
                                <div className="form-group">
                                    <Select
                                        objectValue={true}
                                        name={name}
                                        value={get(values, name, undefined)}
                                        component={fielddefs.object}
                                        storeType="remote"
                                        optionsTemplate={optionsTemplate(props.component || fielddefs.object)}
                                        saveChanges={this.setFieldValue}
                                        label={props.label || name || fielddefs.object}
                                        {...props}
                                        {...this.getFieldProps(name, props)}
                                    />
                                </div>
                            );
                    }
                case "pluginfield":
                    switch (fielddefs.object) {
                        case "url":
                            return (
                                <UrlField {...this.props}/>
                            );
                        case "seo":
                            return (
                                <SeoField {...this.props}/>
                            );
                        case "priority":
                            return (
                                <PriorityField {...this.props}/>
                            );
                        default:
                            return null;
                    }
                // TODO még nincs kész
                case "componentlist":
                    switch (fielddefs.objects) {
                        case "ProductClass":
                            return (
                                <Fragment>
                                    <label className="control-label">
                                        {props.label || name || fielddefs.objects}
                                    </label>
                                    <ProductClassSelect
                                        selectionMode="multiple"
                                        value={get(values, name, undefined)}
                                        saveChanges={this.setFieldValue}
                                        name={name}
                                    />
                                </Fragment>
                            );
                        case "figure":
                            return (
                                <div className="form-group">
                                    <Figures
                                        label={props.label || name || fielddefs.objects}
                                        name={name}
                                        componentConfig={componentConfig}
                                        data={values}
                                        {...props}
                                        {...this.getFieldProps(name, props)}
                                    />
                                </div>
                            )
                        default:
                            return (
                                <div className="form-group">
                                    <SelectorGrid
                                        name={name}
                                        componentConfig={fielddefs.objects}
                                        data={values}
                                        {...props}
                                        {...this.getFieldProps(name, props)}
                                    />
                                </div>
                            );
                    }
            }
        } else {
            return null;
        }
    }
}